import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { PropertyValues } from 'lit';
import { ASC_BUTTON_TAG } from '../../elements/asc-button/asc-button';
import type { AscButton, ButtonSize } from '../../elements/asc-button/asc-button';

/**
 * Groups related `asc-button` elements into a visually contiguous control strip.
 *
 * Cascades a uniform `size` to all slotted buttons and reshapes their border-radius
 * via CSS custom properties so that only the outer edges of the first and last buttons
 * are rounded — creating a flush, pill-ended strip. Variant and theme are intentionally
 * left per-button so consumers can mix styles (e.g., a leading `primary` followed by
 * `secondary` actions).
 *
 * Best suited for toolbars, form action bars, and segmented selection controls.
 *
 * ## Accessibility
 *
 * The host element receives `role="group"` automatically. Consumers **must** supply
 * `aria-label` or `aria-labelledby` to identify the group's purpose to screen readers.
 *
 * ```html
 * <asc-button-group aria-label="Text formatting">
 *   <asc-button variant="secondary">Bold</asc-button>
 *   <asc-button variant="secondary">Italic</asc-button>
 *   <asc-button variant="secondary">Underline</asc-button>
 * </asc-button-group>
 * ```
 *
 * @element asc-button-group
 *
 * @slot - Default slot. Accepts `asc-button` elements. Non-button children are ignored by size-sync logic.
 *
 * @csspart base - Root `<div>` — flex row container for the grouped buttons.
 *
 * @cssprop --asc-button-group-border-radius - Outer corner radius applied to the first and last buttons. Default: `var(--asc-radius-sm)`
 * @cssprop --asc-button-group-gap           - Gap between buttons. Default: `0` (flush strip).
 * @cssprop --asc-button-focus-ring-separator - Inherited by all slotted buttons. Set to `transparent` (default within the group) for a flush focus ring.
 * @cssprop --asc-button-focus-ring-width     - Inherited by all slotted buttons. Set to `2px` (default within the group) to match the visual thickness of the standalone button's ring.
 */
@customElement('asc-button-group')
export class AscButtonGroup extends LitElement {
  static override styles = css`
    :host {
      display: inline-flex;
    }

    .base {
      display: inline-flex;
      flex-direction: row;
      align-items: stretch;
      gap: var(--asc-button-group-gap, 0);
    }

    /*
     * Border-radius shaping via CSS custom property injection.
     * asc-button consumes --asc-button-border-radius for its own radius,
     * so setting it here on ::slotted() elements overrides each button's corners.
     *
     * Single button: no override — native radius retained.
     * First of multiple: round only the left (inline-start) corners.
     * Middle buttons: fully flush on all corners.
     * Last of multiple: round only the right (inline-end) corners.
     */
    ::slotted(asc-button:first-of-type:not(:last-of-type)) {
      --asc-button-border-radius: var(--asc-button-group-border-radius, var(--asc-radius-sm)) 0 0
        var(--asc-button-group-border-radius, var(--asc-radius-sm));
    }

    ::slotted(asc-button:not(:first-of-type):not(:last-of-type)) {
      --asc-button-border-radius: 0;
    }

    ::slotted(asc-button:last-of-type:not(:first-of-type)) {
      --asc-button-border-radius: 0 var(--asc-button-group-border-radius, var(--asc-radius-sm))
        var(--asc-button-group-border-radius, var(--asc-radius-sm)) 0;
    }

    /*
     * Border collapse: adjacent buttons each carry a 1px border which doubles the seam.
     * Pull non-first buttons inline-start by the button border-width token so the overlap
     * stays correct if the token is ever changed or themed.
     * Elevate hovered/focused buttons above siblings with z-index to prevent clipped rings.
     */
    ::slotted(asc-button:not(:first-of-type)) {
      margin-inline-start: calc(-1 * var(--asc-border-width, 1px));
    }

    /*
     * Remove the white separator ring so the focus ring sits flush with the
     * button edge — the default standalone button has a 2px white gap which
     * looks wrong when the button is part of a contiguous strip.
     */
    ::slotted(asc-button) {
      --asc-button-focus-ring-separator: transparent;
      --asc-button-focus-ring-width: 2px;
    }

    ::slotted(asc-button:hover),
    ::slotted(asc-button:focus-within),
    ::slotted(asc-button:focus-visible) {
      position: relative;
      z-index: 1;
    }
  `;

  /** The size applied to all slotted buttons. Matches the size options of `asc-button`. */
  @property({ type: String, reflect: true }) size: ButtonSize = 'md';

  override connectedCallback(): void {
    super.connectedCallback();
    // Apply group role to host so that aria-label/aria-labelledby on the element
    // directly labels the ARIA group without any Shadow DOM forwarding.
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'group');
    }
  }

  private _getButtons(): AscButton[] {
    const slot = this.shadowRoot?.querySelector<HTMLSlotElement>('slot');
    if (!slot) return [];
    return slot
      .assignedElements({ flatten: true })
      .filter((el): el is AscButton => el.localName === ASC_BUTTON_TAG);
  }

  private _syncButtons(): void {
    for (const button of this._getButtons()) {
      button.size = this.size;
    }
  }

  private _handleSlotChange(): void {
    this._syncButtons();
  }

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('size')) {
      this._syncButtons();
    }
  }

  override render() {
    return html`
      <div part="base" class="base">
        <slot @slotchange=${this._handleSlotChange}></slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-button-group': AscButtonGroup;
  }
}
